import { Popover, Select, Space } from "antd";
import React, { useContext, useEffect, useState } from "react";
import trans from "../../../utils";
import { popupcontext } from "../../../context/Popupscontext";
import Popup from "../../Helpers/Popup/Popup";
import { Modal } from "react-bootstrap";
import PopupTitle from "../../Helpers/Popup/PopupParts/PopupTitle";
import Close from "../../Helpers/CloseBtn/Close";
import { toast } from "react-toastify";
import {
  useGetAllMembersQuery,
  useEditTeamMutation,
  useGetSpecificTeamQuery,
  useAssignMemberToTeamMutation,
  useUnassignMemberFromTeamMutation,
} from "../../../Redux/service/Teams/Teams";

export default function AssignMembersToTeamPopup() {
  const {
    isAssigningMembersToTeam,
    setIsAssigningMembersToTeam,
    activeTeamForMembers,
  } = useContext(popupcontext);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [members, setMembers] = useState([]);
  const [assignMemberToTeam] = useAssignMemberToTeamMutation();
  const [unassignMemberFromTeam] = useUnassignMemberFromTeamMutation();

  // Fetch all members
  const { data: allMembersData, refetch: refetchMembers } =
    useGetAllMembersQuery(
      {
        page: 1,
        limit: 10000,
        queryString: "disabled=false",
      },
      { refetchOnMountOrArgChange: true },
    );

  // Fetch team details
  const { data: teamData, refetch: refetchTeam } = useGetSpecificTeamQuery(
    activeTeamForMembers?.id,
    {
      skip: !activeTeamForMembers?.id,
      refetchOnMountOrArgChange: true,
    },
  );

  const [editTeam] = useEditTeamMutation();

  const handleDropdownVisibleChange = (visible) => {
    if (visible) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  // Get members that are already assigned to the team
  useEffect(() => {
    if (
      isAssigningMembersToTeam &&
      teamData?.data?.members &&
      activeTeamForMembers?.id
    ) {
      const teamMemberIds =
        teamData.data.members?.map((member) => member.userId) || [];
      setSelectedMembers(teamMemberIds);
    } else if (!isAssigningMembersToTeam) {
      // Only clear when popup is closed
      setSelectedMembers([]);
    }
  }, [
    teamData?.data?.members,
    activeTeamForMembers?.id,
    isAssigningMembersToTeam,
  ]);

  // Set member options
  useEffect(() => {
    if (allMembersData?.data) {
      setMembers(
        allMembersData.data.map((member) => ({
          value: member.id,
          label: `${member.name} (${member.email})`,
          code: member.id,
        })),
      );
    }
  }, [allMembersData?.data]);

  const handleAssign = async (memberId) => {
    try {
      await assignMemberToTeam({
        teamId: activeTeamForMembers.id,
        memberId: memberId,
      }).unwrap();
      toast.success(trans("Member assigned to team successfully"), {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar",
      });
      await refetchTeam();
      await refetchMembers();
    } catch (error) {
      setSelectedMembers((prev) => prev.filter((v) => v !== memberId));
      toast.error(
        error?.data?.message || trans("Failed to assign member to team"),
        {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar",
        },
      );
    }
  };

  const handleUnAssign = async (memberId) => {
    try {
      await unassignMemberFromTeam({
        teamId: activeTeamForMembers.id,
        memberId: memberId,
      }).unwrap();
      toast.success(trans("Member unassigned from team successfully"), {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar",
      });
      await refetchTeam();
      await refetchMembers();
    } catch (error) {
      toast.error(
        error?.data?.message || trans("Failed to unassign member from team"),
        {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar",
        },
      );
    }
  };

  // Reset selected members only when popup closes
  useEffect(() => {
    if (!isAssigningMembersToTeam) {
      // Clear selection when popup closes
      setSelectedMembers([]);
    }
  }, [isAssigningMembersToTeam]);

  // Refetch team data when popup opens to get latest members
  useEffect(() => {
    if (isAssigningMembersToTeam && activeTeamForMembers?.id) {
      refetchTeam();
    }
  }, [isAssigningMembersToTeam, activeTeamForMembers?.id, refetchTeam]);

  const teamName = teamData?.data?.name || teamData?.data?.code || "";

  return (
    <Popup show={isAssigningMembersToTeam} size="lg" style={"popup-bodyscroll"}>
      <Modal.Header className="modal-header flex-between">
        <Modal.Title id="contained-modal-title-vcenter">
          <PopupTitle headtitle={trans("Assign Members")} subtitle={teamName} />
        </Modal.Title>
        <Close
          style="close-32 close"
          close={() => {
            setIsAssigningMembersToTeam(false);
          }}
          img="Close"
        />
      </Modal.Header>
      <Modal.Body className="modal-body-scrollable">
        <div>
          <div className="mult-input-label">{trans("Members")}</div>
          <Space
            style={{
              width: "100%",
            }}
            direction="vertical"
          >
            <Select
              mode="multiple"
              style={{
                width: "100%",
                marginTop: "8px",
              }}
              maxTagCount={"responsive"}
              placeholder={trans("Please select members")}
              onSelect={(value) => {
                setSelectedMembers((prev) => [...prev, value]);
                handleAssign(value);
              }}
              onDeselect={(value) => {
                setSelectedMembers((prev) => prev.filter((v) => v !== value));
                handleUnAssign(value);
              }}
              value={selectedMembers}
              options={members}
              open={isOpen}
              onDropdownVisibleChange={handleDropdownVisibleChange}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              optionRender={(option) => (
                <Popover
                  placement="right"
                  content={
                    <div>
                      {trans("Member Code")}: {option.data.code}
                    </div>
                  }
                >
                  <div style={{ cursor: "pointer" }}>{option.label}</div>
                </Popover>
              )}
            />
          </Space>
        </div>
      </Modal.Body>
    </Popup>
  );
}
